import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  CheckCircle2, Phone, MessageCircle, ShieldCheck, Calculator,
  Award, Clock, Wallet, ChevronDown, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import { getServiceBySlug, CATEGORIES } from '../data/servicesData';
import { getCalcConfig } from '../data/calculatorConfigs';
import { usePricingConfig } from '../data/pricingConfig';
import { SiteHeader, SiteFooter } from './ServicesIndex';
import ServiceCalculator from '../components/ServiceCalculator';

const QUICK_FACTS = [
  { Icon: Award, label: 'Certified Experts' },
  { Icon: ShieldCheck, label: 'Govt-Approved' },
  { Icon: Clock, label: 'Same-Day Slots' },
  { Icon: Wallet, label: 'Transparent Pricing' },
];

export default function PricingPage({ onOpenCalculator }) {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const staticConfig = getCalcConfig(slug);
  const pricing = usePricingConfig();
  // Base = static (structural fields), overlay DB-managed rate values so admin edits reflect live.
  const dbService = pricing.services?.[slug];
  const config = staticConfig ? { ...staticConfig, ...(dbService || {}) } : null;
  const [mobileCalcOpen, setMobileCalcOpen] = useState(false);

  useSeo(
    service ? `${service.name} — Pricing & Instant Quote` : undefined,
    service ? `Get instant pricing for ${service.name}. ${service.shortDesc}` : undefined,
  );

  if (!service || !config) return <Navigate to="/services" replace />;

  const category = CATEGORIES.find((c) => c.id === service.category);

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-3 text-xs sm:text-sm text-gray-500 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-green-700">Home</Link>
          <span className="mx-2">›</span>
          <span className="hover:text-green-700">Pricing</span>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">{service.name}</span>
        </div>
      </div>

      {/* HERO */}
      <section
        className="relative overflow-hidden text-white"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(10,37,64,0.94) 0%, rgba(30,58,95,0.86) 60%, rgba(0,168,107,0.72) 100%), url(${service.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-10 sm:py-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 px-3 py-1 text-[11px] sm:text-xs font-semibold">
            {category?.label} · Pricing
          </span>
          <h1 className="mt-3 text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl" data-testid="pricing-title">
            {service.name}
          </h1>
          <p className="mt-2 text-sm sm:text-lg text-white/85 max-w-2xl">{service.tagline}</p>
        </div>
      </section>

      {/* MAIN: 2-COL DESKTOP, STACKED MOBILE */}
      <main className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-8 lg:py-12 pb-32 lg:pb-12">
        <div className="lg:grid lg:grid-cols-12 lg:gap-10">
          {/* LEFT — DETAILS (60%) */}
          <div className="lg:col-span-7 space-y-8">
            {/* Quick facts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {QUICK_FACTS.map(({ Icon, label }) => (
                <div key={label} className="bg-white rounded-xl px-3 py-3 ring-1 ring-gray-100 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                    <Icon size={16} className="text-green-700" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700 leading-tight">{label}</span>
                </div>
              ))}
            </div>

            {/* About */}
            <section className="bg-white rounded-2xl p-5 sm:p-7 ring-1 ring-gray-100 shadow-sm">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">About {service.name}</h2>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{service.about.what}</p>
              <h3 className="text-base font-semibold text-gray-900 mt-5 mb-2">Why it matters</h3>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{service.about.danger}</p>

              <h3 className="text-base font-semibold text-gray-900 mt-5 mb-3">Commonly found in</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {service.about.common.map((loc) => (
                  <li key={loc} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 size={15} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{loc}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Benefits */}
            <section className="bg-white rounded-2xl p-5 sm:p-7 ring-1 ring-gray-100 shadow-sm">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Key Benefits</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {service.signs.map((sign, i) => (
                  <li key={i} className="flex items-start gap-3 bg-slate-50 rounded-xl p-3">
                    <div className="w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
                    <span className="text-sm text-gray-700">{sign}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Process */}
            <section className="bg-white rounded-2xl p-5 sm:p-7 ring-1 ring-gray-100 shadow-sm">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Our Treatment Process</h2>
              <p className="text-sm text-gray-500 mb-5">Transparent, documented, end-to-end</p>
              <ol className="space-y-4">
                {service.process.map((step, i) => (
                  <li key={i} className="flex gap-4">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white font-bold flex items-center justify-center shadow-sm text-sm">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{step.title}</h3>
                      <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">{step.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* FAQs */}
            <section className="bg-white rounded-2xl p-5 sm:p-7 ring-1 ring-gray-100 shadow-sm">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <div className="space-y-2">
                {service.faqs.map((f, i) => <FaqItem key={i} idx={i} faq={f} />)}
              </div>
            </section>
          </div>

          {/* RIGHT — STICKY CALCULATOR (DESKTOP ONLY) */}
          <aside className="hidden lg:block lg:col-span-5">
            <div
              className="sticky top-[100px] max-h-[calc(100vh-120px)] overflow-y-auto rounded-2xl"
              style={{ WebkitOverflowScrolling: 'touch' }}
              data-testid="pricing-sidebar-calc"
            >
              <ServiceCalculator service={service} config={config} variant="sidebar" areaLimit={pricing.areaLimits?.residential} />
              <div className="mt-3 mb-1 flex items-center justify-center gap-3 text-xs text-gray-500">
                <a href="tel:+917819988010" className="flex items-center gap-1 hover:text-green-700">
                  <Phone size={12} /> +91-7819988010
                </a>
                <span>·</span>
                <a href="https://api.whatsapp.com/send?phone=919410110910" target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-green-700">
                  <MessageCircle size={12} /> WhatsApp
                </a>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <SiteFooter />

      {/* MOBILE STICKY BOTTOM CTA */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-6px_24px_rgba(0,0,0,0.08)] px-3 py-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
        data-testid="pricing-mobile-cta-bar"
      >
        <div className="flex items-center gap-2 max-w-3xl mx-auto">
          <a
            href="tel:+917819988010"
            aria-label="Call EntoRid"
            data-testid="pricing-mobile-call"
            className="flex-shrink-0 w-12 h-12 min-h-[48px] rounded-xl bg-green-50 text-green-700 border border-green-200 flex items-center justify-center hover:bg-green-100 active:scale-95 transition"
          >
            <Phone size={20} />
          </a>
          <a
            href="https://api.whatsapp.com/send?phone=919410110910"
            target="_blank"
            rel="noreferrer"
            aria-label="WhatsApp EntoRid"
            data-testid="pricing-mobile-wa"
            className="flex-shrink-0 w-12 h-12 min-h-[48px] rounded-xl bg-[#25D366]/10 text-[#1da851] border border-[#25D366]/30 flex items-center justify-center hover:bg-[#25D366]/20 active:scale-95 transition"
          >
            <MessageCircle size={20} />
          </a>
          <Button
            onClick={() => setMobileCalcOpen(true)}
            data-testid="pricing-mobile-open-calc"
            className="flex-1 h-12 min-h-[48px] px-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg active:scale-[0.98]"
          >
            <Calculator size={18} className="mr-2" />
            Calculate Price
          </Button>
        </div>
      </div>

      {/* MOBILE SLIDE-UP CALCULATOR PANEL */}
      {mobileCalcOpen && (
        <div className="lg:hidden fixed inset-0 z-[70]" data-testid="pricing-mobile-calc-sheet">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/55 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setMobileCalcOpen(false)}
          />
          {/* Sheet */}
          <div
            className="absolute bottom-0 left-0 right-0 max-h-[92vh] bg-slate-50 rounded-t-3xl shadow-2xl flex flex-col animate-in slide-in-from-bottom duration-300"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {/* Drag handle + close */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 pt-3 pb-2 bg-slate-50 rounded-t-3xl">
              <div className="absolute left-1/2 -translate-x-1/2 top-2 w-10 h-1 rounded-full bg-gray-300" />
              <div className="text-sm font-bold text-gray-900 mt-3">Get Instant Quote</div>
              <button
                onClick={() => setMobileCalcOpen(false)}
                className="mt-2 p-2 -mr-2 text-gray-500 hover:text-gray-900 active:scale-95"
                aria-label="Close"
                data-testid="pricing-mobile-calc-close"
              >
                <X size={22} />
              </button>
            </div>
            <div
              className="overflow-y-auto px-4 pb-6"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <ServiceCalculator service={service} config={config} variant="sheet" areaLimit={pricing.areaLimits?.residential} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FaqItem({ idx, faq }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="bg-slate-50 rounded-xl ring-1 ring-gray-100 overflow-hidden" data-testid={`pricing-faq-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-slate-100 transition-colors"
      >
        <span className="font-semibold text-gray-900 text-sm">{faq.q}</span>
        <ChevronDown size={18} className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 text-sm text-gray-700 leading-relaxed border-t border-gray-100">
          {faq.a}
        </div>
      )}
    </div>
  );
}
